<script>
import Lottie from "@/components/widgets/lottie.vue";
import animationData from "@/components/widgets/lupuorrc.json";
import { authComputed } from '@/state/helpers'

export default {
  name: "TwoFactorSetupModal",
  props: {
    showCancel: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    lottie: Lottie,
  },

  data() {
    return {
      step: "qr",
      qrCode: null,
      secretKey: null,
      verificationCode: "",
      recoveryCodes: [],
      loading: false,
      tempToken: null,
      lottieOptions: {
        animationData: animationData
      },
      isReSetupMode: false
    };
  },

  computed: {
    ...authComputed,
    isLoggedIn() {
      return this.loggedIn;
    },
    setupApi() {
      return this.isLoggedIn
          ? '/service/centralize-authenticate/api/tenant/v1/security/2fa/setup'
          : '/service/centralize-authenticate/api/tenant/v1/security/2fa/login/setup';
    },
    reSetupApi() {
      return '/service/centralize-authenticate/api/tenant/v1/security/2fa/re-setup';
    },
    enableApi() {
      return this.isLoggedIn
          ? '/service/centralize-authenticate/api/tenant/v1/security/2fa/enable'
          : '/service/centralize-authenticate/api/tenant/v1/security/2fa/login/enable';
    },
  },

  methods: {
    async openModal(data = null, mode = 'setup') {
      this.resetState();
      this.tempToken = data?.temp_token || '';
      this.loading = true;

      let apiUrl = '';
      if (mode === 're-setup') {
        apiUrl = this.reSetupApi;
        this.isReSetupMode = true;
      } else {
        apiUrl = this.setupApi;
      }

      // Fetch 2fa initialize setup
      try {
        const response = await this.$axios.post(apiUrl,
            {},
            {
              headers: {
                'X-Temp-Token': this.tempToken
              }
            }
        );

        if (response.data.status) {
          // Only show modal after success
          this.$refs.twoFactorSetupModal.show();

          // simulate short UX delay
          await new Promise(resolve => setTimeout(resolve, 500));

          const data = response.data.data;
          this.qrCode = data.qr_data_uri;
          this.secretKey = data.secret;
        } else {
          this.toastError("Error", response.data.message);
          this.closeModal(); // close on failure
        }
      } catch (error) {
        this.errorHandler(error);
        this.closeModal(); // close on exception
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      this.resetState();
      this.$emit("closed");
      // Notify axios interceptor if cancelled
      window.dispatchEvent(new CustomEvent('2fa-setup-completed', {
        detail: { success: false, cancelled: true }
      }));
      this.$refs.twoFactorSetupModal.hide();
    },

    resetState() {
      this.step = "qr"; // qr, recovery, completed
      this.qrCode = null;
      this.secretKey = null;
      this.verificationCode = "";
      this.recoveryCodes = [];
      this.loading = false;
    },

    async verifyCode() {
      if (this.loading) return;

      if (!/^\d{6}$/.test(this.verificationCode)) {
        return this.toastError('Error', this.$t('t-enter-invalid-code'));
      }

      this.loading = true;

      try {
        const response = await this.$axios.post(this.enableApi,
            {
              otp: this.verificationCode
            },
            {
              headers: {
                'X-Temp-Token': this.tempToken
              }
            }
        );

        if (response.data.status) {
          this.toastSuccess('Success', response.data.message);
          this.recoveryCodes = response.data.data.recovery_codes;
          this.step = "recovery";
        } else {
          this.toastError('Error', response.data.message);
        }
      } catch (error) {
        this.errorHandler(error);
      } finally {
        this.loading = false;
      }
    },

    finishRecoveryCodes() {
      this.step = "completed";
    },

    finishSetup() {
      this.$emit('completed', { success: true }); // already in on2faSetupCompleted
      window.dispatchEvent(new CustomEvent('2fa-setup-completed', {
        detail: { success: true }
      }));
      this.closeModal();
    },

    async copyAllCodes() {
      if (!navigator.clipboard) {
        return this.toastError("Error", "Clipboard not supported on this browser");
      }

      try {
        await navigator.clipboard.writeText(this.recoveryCodes.join('\n'));
        this.toastSuccess("Copied", "All recovery codes copied to clipboard!");
      } catch (e) {
        this.toastError("Error", "Failed to copy recovery codes");
      }
    },

    downloadCodes() {
      const blob = new Blob([this.recoveryCodes.join("\n")], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "recovery-codes.txt";
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
}
</script>

<template>
  <BModal
      ref="twoFactorSetupModal"
      centered
      modal-class="flip"
      class="v-modal-custom"
      hide-footer
      header-class="border-bottom pb-3"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      style="z-index: 2000"
  >
    <template #title>
      <h5 class="modal-title" id="twoFactorModalLabel">
        {{ isReSetupMode ? $t('t-re-setup') : $t('t-enable') }} {{ $t('t-two-factor-auth') }}
      </h5>
    </template>

    <div class="modal-body text-center">
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border text-primary" role="status"></div>
      </div>

      <!-- Step 1: QR Code -->
      <div v-if="!loading && step === 'qr'">
        <h5>{{ isReSetupMode ? $t('t-re-setup') : $t('t-setup') }} {{ $t('t-two-factor-auth') }}</h5>
        <p class="text-muted mb-3">{{ $t('t-two-factor-setup-desc') }}</p>

        <div class="shadow-lg rounded-4 p-3 bg-white d-inline-block">
          <img
              :src="qrCode"
              alt="2FA QR Code"
              class="img-fluid rounded"
              style="max-width: 260px;"
          />
        </div>

        <p class="small text-muted mt-2">
          {{ $t('t-manual-enter-2fa') }}: <strong>{{ secretKey }}</strong>
        </p>

        <div
            class="alert alert-warning py-2 px-3 d-inline-flex align-items-center mt-2"
            role="alert"
        >
          <i class="ri-time-line me-2 fs-5 text-warning"></i>
          <small>{{ $t('t-qr-code-validity-note', { minutes: 10 }) }}</small>
        </div>

        <div class="mt-4">
          <input
              id="auth-code"
              v-model="verificationCode"
              type="text"
              inputmode="numeric"
              maxlength="6"
              class="form-control text-center fw-semibold"
              :placeholder="$t('t-enter-6-digit-code')"
              aria-label="Enter 6-digit authentication code"
              @keyup.enter="verifyCode"
          />
        </div>

        <BButton
            variant="primary"
            class="mt-3 w-100"
            @click="verifyCode"
            :disabled="loading || verificationCode.length < 6"
        >
          <i class="mdi mdi-lock-check-outline fs-5"></i> {{ $t('t-verify-and-enable') }}
        </BButton>
      </div>

      <!-- Step 2: Recovery Codes -->
      <div v-else-if="step === 'recovery'">
        <h5>{{ $t('t-download-recovery-codes') }}</h5>
        <p class="text-muted mb-3">
          {{ $t('t-recovery-code-desc') }}
        </p>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <p class="fw-bold mb-0">{{ $t('t-save-recovery-codes') }}</p>
          <div>
            <BButton size="sm" variant="outline-secondary" @click="copyAllCodes" class="me-2">
              <i class="ri-file-copy-2-line fs-6"></i>
            </BButton>
            <BButton size="sm" variant="outline-primary" @click="downloadCodes">
              <i class="ri-download-2-line fs-6"></i>
            </BButton>
          </div>
        </div>

        <div class="bg-light border rounded p-3 mb-2">
          <ul class="list-unstyled mb-0">
            <li v-for="code in recoveryCodes" :key="code" class="py-1">
              <span class="fs-5 text-primary-emphasis">{{ code }}</span>
            </li>
          </ul>
        </div>

        <p class="small text-danger-emphasis">
          {{ $t('t-recovery-code-warning') }}
        </p>

        <button class="btn btn-success mt-3 w-100" @click="finishRecoveryCodes" :disabled="loading">
          <i class="mdi mdi-content-save-outline fs-5"></i>
          {{ $t('t-saved-my-codes') }}
        </button>
      </div>

      <!-- Step 3: Setup Completed -->
      <div v-else-if="step === 'completed'">
        <div class="mb-4">
          <lottie
              colors="primary:#121331,secondary:#08a88a"
              trigger="loop"
              :options="lottieOptions"
              :height="120"
              :width="120"
              role="img"
              aria-label="Success animation"
          />
        </div>

        <h5>{{ $t('t-two-factor-setup-completed') }}</h5>

        <div
            class="alert alert-success py-2 px-3 mt-3 d-inline-flex align-items-center"
            role="alert"
        >
          <i class="ri-shield-check-line me-2 fs-5"></i>
          <small>{{ $t('t-2fa-enabled-success') }}</small>
        </div>

        <BButton variant="success" class="mt-3 w-100" @click="finishSetup" :disabled="loading">
          <i class="mdi mdi-check fs-5"></i> {{ $t('t-done') }}
        </BButton>
      </div>
    </div>

    <div v-if="showCancel && !loading && step === 'qr'" class="modal-footer border-top-0">
      <BButton variant="light" class="w-100" @click="closeModal">
        <i class="ri-close-line fs-16"></i> {{ $t('t-cancel') }}
      </BButton>
    </div>
  </BModal>
</template>

<style scoped>
.v-modal-custom {
  z-index: 99999 !important;
}

.modal-backdrop {
  z-index: 99998 !important;
}
</style>